/**
 * SettingsProvider - Settings Data Context for V2 Dashboard
 *
 * Manages settings-related data (users, invitations, speakers, tags) for V2 components.
 * Fetches data directly from vb-server API (no Redux dependency).
 *
 * Data is loaded lazily when the provider mounts and the org is available.
 * Provides refresh functions for each data type.
 *
 * Usage:
 *   const { users, speakers, tags, invitations, refreshUsers } = useSettings();
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from 'react';
import { useOrg } from './OrgProvider';
import { listOrgUsers, OrgUser, getCurrentUser, User } from '../../api/server/users';
import {
  listSpeakers,
  Speaker,
  listSpeakerRadioIds,
  SpeakerRadioId
} from '../../api/server/speakers';
import { listTags, Tag } from '../../api/server/tags';
import { listInvites, Invite } from '../../api/server/invites';
import { logger } from '../../utils/logger';

interface SettingsContextType {
  /** Current authenticated user */
  currentUser: User | null;
  /** Users in the current organization */
  users: OrgUser[];
  /** Map of users by user_id for quick lookup */
  usersMap: Record<string, OrgUser>;
  /** Speakers in the current organization */
  speakers: Speaker[];
  /** Map of speakers by speaker_id for quick lookup */
  speakersMap: Record<string, Speaker>;
  /** Tags in the current organization (includes global tags) */
  tags: Tag[];
  /** Map of tags by tag_id for quick lookup */
  tagsMap: Record<string, Tag>;
  /** Pending invitations in the current organization */
  invitations: Invite[];
  /** Map of invitations by invite_id for quick lookup */
  invitationsMap: Record<string, Invite>;
  /** Whether settings data is loading */
  isLoading: boolean;
  /** Refresh current user from server */
  refreshCurrentUser: () => Promise<void>;
  /** Refresh users from server */
  refreshUsers: () => Promise<void>;
  /** Speaker-to-radio ID mappings for the current organization */
  speakerRadioIds: SpeakerRadioId[];
  /** Map of radio_id -> Speaker for quick lookup */
  radioIdToSpeakerMap: Record<string, Speaker>;
  /** Refresh speakers from server */
  refreshSpeakers: () => Promise<void>;
  /** Refresh speaker radio ID mappings from server */
  refreshSpeakerRadioIds: () => Promise<void>;
  /** Refresh tags from server */
  refreshTags: () => Promise<void>;
  /** Refresh invitations from server */
  refreshInvitations: () => Promise<void>;
  /** Refresh all settings data */
  refreshAll: () => Promise<void>;
}

// Export for test utilities
export const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: React.ReactNode;
}

export function SettingsProvider({ children }: SettingsProviderProps) {
  const { currentOrg } = useOrg();

  // Track previous org to detect changes
  const prevOrgIdRef = useRef<string | null>(null);

  // Data state
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [speakerRadioIds, setSpeakerRadioIds] = useState<SpeakerRadioId[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [invitations, setInvitations] = useState<Invite[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Maps for quick lookup
  const usersMap = useMemo(() => {
    const map: Record<string, OrgUser> = {};
    for (const user of users) {
      map[user.user_id] = user;
    }
    return map;
  }, [users]);

  const speakersMap = useMemo(() => {
    const map: Record<string, Speaker> = {};
    for (const speaker of speakers) {
      map[speaker.speaker_id] = speaker;
    }
    return map;
  }, [speakers]);

  const radioIdToSpeakerMap = useMemo(() => {
    const map: Record<string, Speaker> = {};
    for (const mapping of speakerRadioIds) {
      const speaker = speakersMap[mapping.speaker_id];
      if (speaker) map[mapping.radio_id] = speaker;
    }
    return map;
  }, [speakerRadioIds, speakersMap]);

  const tagsMap = useMemo(() => {
    const map: Record<string, Tag> = {};
    for (const tag of tags) {
      map[tag.tag_id] = tag;
    }
    return map;
  }, [tags]);

  const invitationsMap = useMemo(() => {
    const map: Record<string, Invite> = {};
    for (const invite of invitations) {
      map[invite.invite_id] = invite;
    }
    return map;
  }, [invitations]);

  // Refresh functions
  const refreshCurrentUser = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      setCurrentUser(user);
      logger.debug('[SettingsProvider] Current user loaded');
    } catch (error) {
      logger.error('[SettingsProvider] Failed to load current user:', error);
    }
  }, []);

  const refreshUsers = useCallback(async () => {
    if (!currentOrg?.org_id) return;
    try {
      const usersList = await listOrgUsers(currentOrg.org_id);
      setUsers(usersList);
      logger.debug('[SettingsProvider] Users loaded:', usersList.length);
    } catch (error) {
      logger.error('[SettingsProvider] Failed to load users:', error);
      setUsers([]);
    }
  }, [currentOrg?.org_id]);

  const refreshSpeakers = useCallback(async () => {
    if (!currentOrg?.org_id) return;
    try {
      const speakersList = await listSpeakers(currentOrg.org_id);
      setSpeakers(speakersList);
      logger.debug('[SettingsProvider] Speakers loaded:', speakersList.length);
    } catch (error) {
      logger.error('[SettingsProvider] Failed to load speakers:', error);
      setSpeakers([]);
    }
  }, [currentOrg?.org_id]);

  const refreshSpeakerRadioIds = useCallback(async () => {
    if (!currentOrg?.org_id) return;
    try {
      const mappings = await listSpeakerRadioIds(currentOrg.org_id);
      setSpeakerRadioIds(mappings);
      logger.debug('[SettingsProvider] Speaker radio IDs loaded:', mappings.length);
    } catch (error) {
      logger.error('[SettingsProvider] Failed to load speaker radio IDs:', error);
      setSpeakerRadioIds([]);
    }
  }, [currentOrg?.org_id]);

  const refreshTags = useCallback(async () => {
    if (!currentOrg?.org_id) return;
    try {
      const tagsList = await listTags(currentOrg.org_id);
      setTags(tagsList);
      logger.debug('[SettingsProvider] Tags loaded:', tagsList.length);
    } catch (error) {
      logger.error('[SettingsProvider] Failed to load tags:', error);
      setTags([]);
    }
  }, [currentOrg?.org_id]);

  const refreshInvitations = useCallback(async () => {
    if (!currentOrg?.org_id) return;
    try {
      const invitesList = await listInvites(currentOrg.org_id);
      setInvitations(invitesList);
      logger.debug('[SettingsProvider] Invitations loaded:', invitesList.length);
    } catch (error) {
      logger.error('[SettingsProvider] Failed to load invitations:', error);
      setInvitations([]);
    }
  }, [currentOrg?.org_id]);

  const refreshAll = useCallback(async () => {
    if (!currentOrg?.org_id) return;
    setIsLoading(true);
    try {
      await Promise.all([
        refreshCurrentUser(),
        refreshUsers(),
        refreshSpeakers(),
        refreshSpeakerRadioIds(),
        refreshTags(),
        refreshInvitations()
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentOrg?.org_id,
    refreshCurrentUser,
    refreshUsers,
    refreshSpeakers,
    refreshSpeakerRadioIds,
    refreshTags,
    refreshInvitations
  ]);

  // Load data when org changes
  useEffect(() => {
    if (!currentOrg?.org_id) return;

    // Only trigger if org actually changed
    if (prevOrgIdRef.current === currentOrg.org_id) return;
    prevOrgIdRef.current = currentOrg.org_id;

    logger.info('[SettingsProvider] Loading settings data for org:', currentOrg.name);
    refreshAll();
  }, [currentOrg, refreshAll]);

  // Load current user on mount
  useEffect(() => {
    refreshCurrentUser();
  }, [refreshCurrentUser]);

  const value = useMemo<SettingsContextType>(
    () => ({
      currentUser,
      users,
      usersMap,
      speakers,
      speakersMap,
      speakerRadioIds,
      radioIdToSpeakerMap,
      tags,
      tagsMap,
      invitations,
      invitationsMap,
      isLoading,
      refreshCurrentUser,
      refreshUsers,
      refreshSpeakers,
      refreshSpeakerRadioIds,
      refreshTags,
      refreshInvitations,
      refreshAll
    }),
    [
      currentUser,
      users,
      usersMap,
      speakers,
      speakersMap,
      speakerRadioIds,
      radioIdToSpeakerMap,
      tags,
      tagsMap,
      invitations,
      invitationsMap,
      isLoading,
      refreshCurrentUser,
      refreshUsers,
      refreshSpeakers,
      refreshSpeakerRadioIds,
      refreshTags,
      refreshInvitations,
      refreshAll
    ]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

/**
 * Hook to access settings context
 */
export function useSettings(): SettingsContextType {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

export default SettingsProvider;
